import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://capitalbillingservices.com';
const LOGO_URL = `${BASE_URL}/assets/images/cbs-logo.png`;

// Organization Schema - Core business identity
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    "name": "Capital Billing Services",
    "alternateName": "CBS",
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": LOGO_URL,
      "width": 300,
      "height": 100
    },
    "image": LOGO_URL,
    "description": "Capital Billing Services is a family-owned medical billing and practice management company with 25+ years of healthcare experience, serving providers nationwide.",
    "foundingDate": "1999",
    "founder": {
      "@type": "Person",
      "name": "James Irungu"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Atlanta",
      "addressRegion": "GA",
      "addressCountry": "US"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-470-944-9277",
        "contactType": "customer service",
        "email": "info@capitalbillingservices.com",
        "availableLanguage": ["English"]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/capitalbillingservices",
      "https://twitter.com/capitalbilling",
      "https://www.linkedin.com/company/capital-billing-services"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 10,
      "maxValue": 50
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// LocalBusiness Schema - For local SEO with service areas
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": "Capital Billing Services",
    "image": LOGO_URL,
    "url": BASE_URL,
    "telephone": "+1-470-944-9277",
    "email": "info@capitalbillingservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Atlanta",
      "addressRegion": "GA",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.7490,
      "longitude": -84.3880
    },
    "areaServed": [
      {
        "@type": "State",
        "name": "Georgia",
        "sameAs": "https://en.wikipedia.org/wiki/Georgia_(U.S._state)"
      },
      {
        "@type": "State",
        "name": "Texas",
        "sameAs": "https://en.wikipedia.org/wiki/Texas"
      },
      {
        "@type": "State",
        "name": "California",
        "sameAs": "https://en.wikipedia.org/wiki/California"
      },
      {
        "@type": "State",
        "name": "North Carolina",
        "sameAs": "https://en.wikipedia.org/wiki/North_Carolina"
      },
      {
        "@type": "Country",
        "name": "United States"
      }
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "priceRange": "$$",
    "serviceType": [
      "Medical Billing",
      "Revenue Cycle Management",
      "Medical Credentialing",
      "Provider Enrollment",
      "Denial Management",
      "Practice Consulting"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// MedicalBusiness Schema - Healthcare-specific
export const MedicalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${BASE_URL}/#medicalbusiness`,
    "name": "Capital Billing Services",
    "description": "Expert medical billing, credentialing, and revenue cycle management services for healthcare providers including anesthesiology, dermatology, mental health, OB-GYN, oncology, and more.",
    "url": BASE_URL,
    "logo": LOGO_URL,
    "image": LOGO_URL,
    "telephone": "+1-470-944-9277",
    "email": "info@capitalbillingservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Atlanta",
      "addressRegion": "GA",
      "addressCountry": "US"
    },
    "medicalSpecialty": [
      "Anesthesiology",
      "Dermatology",
      "Mental Health",
      "Obstetrics and Gynecology",
      "Oncology",
      "Orthopedics",
      "Pathology",
      "Plastic Surgery",
      "Hospital Medicine"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ProfessionalService Schema
export const ProfessionalServiceSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}/#professionalservice`,
    "name": "Capital Billing Services",
    "description": "Professional medical billing, credentialing, consulting, and revenue cycle management services.",
    "url": BASE_URL,
    "logo": LOGO_URL,
    "telephone": "+1-470-944-9277",
    "email": "info@capitalbillingservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Atlanta",
      "addressRegion": "GA",
      "addressCountry": "US"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Medical Billing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Medical Billing",
            "description": "Complete medical billing services including claim submission, follow-up, and denial management."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Credentialing Services",
            "description": "Provider enrollment and credentialing with insurance networks nationwide."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Revenue Cycle Management",
            "description": "End-to-end revenue cycle management to maximize practice revenue."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Practice Consulting",
            "description": "Healthcare consulting services to optimize practice operations."
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "50",
      "bestRating": "5"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema - For individual service pages
export const ServiceSchema = ({ name, description, url, provider = "Capital Billing Services" }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${BASE_URL}${url}`,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "url": BASE_URL
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "serviceType": "Medical Billing Services"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema - For FAQ pages
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Article/BlogPosting Schema - For blog posts
export const BlogPostingSchema = ({ 
  title, 
  description, 
  url, 
  datePublished, 
  dateModified,
  author = "Capital Billing Services",
  image = LOGO_URL
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": image,
    "url": `${BASE_URL}${url}`,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}${url}`
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Case Study Schema (using Article type)
export const CaseStudySchema = ({
  title,
  description,
  url,
  datePublished,
  client,
  results
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "articleType": "Case Study",
    "headline": title,
    "description": description,
    "url": `${BASE_URL}${url}`,
    "datePublished": datePublished,
    "author": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "about": {
      "@type": "Thing",
      "name": client
    },
    "abstract": results
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Review/Testimonial Schema
export const ReviewSchema = ({ reviews }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Capital Billing Services",
    "url": BASE_URL,
    "review": reviews.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewBody": review.text,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating || 5,
        "bestRating": 5
      },
      "datePublished": review.date
    })),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": reviews.length.toString(),
      "bestRating": "5"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Person Schema - For leadership team
export const PersonSchema = ({ person }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": person.name,
    "jobTitle": person.role,
    "description": person.bio,
    "image": person.image?.startsWith('http') ? person.image : `${BASE_URL}${person.image}`,
    "worksFor": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "url": BASE_URL
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Team Schema - Multiple persons
export const TeamSchema = ({ members }) => {
  const schema = members.map(person => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "name": person.name,
    "jobTitle": person.role,
    "description": person.bio?.substring(0, 200),
    "image": person.image?.startsWith('http') ? person.image : `${BASE_URL}${person.image}`,
    "worksFor": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "url": BASE_URL
    }
  }));

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Video Schema
export const VideoSchema = ({ 
  name, 
  description, 
  thumbnailUrl, 
  contentUrl, 
  uploadDate,
  duration 
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl || LOGO_URL,
    "contentUrl": contentUrl?.startsWith('http') ? contentUrl : `${BASE_URL}${contentUrl}`,
    "uploadDate": uploadDate,
    "duration": duration,
    "publisher": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// HowTo Schema - For process explanations
export const HowToSchema = ({ 
  name, 
  description, 
  steps,
  totalTime,
  image = LOGO_URL
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "image": image,
    "totalTime": totalTime,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      "url": step.url ? `${BASE_URL}${step.url}` : undefined
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebSite Schema - For sitelinks search box
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Capital Billing Services",
    "url": BASE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${BASE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Contact Page Schema
export const ContactPageSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Capital Billing Services",
    "description": "Contact Capital Billing Services for medical billing, credentialing, and revenue cycle management services.",
    "url": `${BASE_URL}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "telephone": "+1-470-944-9277",
      "email": "info@capitalbillingservices.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Atlanta",
        "addressRegion": "GA",
        "addressCountry": "US"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Combined Homepage Schema
export const HomePageSchema = () => {
  return (
    <>
      <OrganizationSchema />
      <LocalBusinessSchema />
      <MedicalBusinessSchema />
      <ProfessionalServiceSchema />
      <WebSiteSchema />
    </>
  );
};

// Blog Post Schema for individual articles
export const BlogPostSchema = ({ title, description, url, datePublished, author = "Capital Billing Services" }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "url": `${BASE_URL}${url}`,
    "datePublished": datePublished,
    "dateModified": datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Capital Billing Services",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}${url}`
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default {
  OrganizationSchema,
  LocalBusinessSchema,
  MedicalBusinessSchema,
  ProfessionalServiceSchema,
  ServiceSchema,
  FAQSchema,
  BlogPostingSchema,
  BlogPostSchema,
  CaseStudySchema,
  ReviewSchema,
  PersonSchema,
  TeamSchema,
  BreadcrumbSchema,
  VideoSchema,
  HowToSchema,
  WebSiteSchema,
  ContactPageSchema,
  HomePageSchema
};
